/**
 * Schedule a meeting — the statutory rule card changes with the kind picked
 * (MC 3 clear days / AGM·SGM 14 clear days, quorum formula, cadence), so the
 * person scheduling SEES the law before the server enforces it. Agenda items
 * are mandatory (the notice must state the business) and can carry a
 * conflict-of-interest recusal flag (bye-law 120).
 * Wired: POST /api/meetings.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, IconButton, Input, Screen, TopBar } from '@/ui';

const KINDS: { key: 'MC' | 'AGM' | 'SGM'; label: string; noticeDays: number; rule: string; quorum: string }[] = [
  { key: 'MC', label: 'Committee meeting', noticeDays: 3,
    rule: "Bye-law 132 — 3 clear days' notice with date, time, place and business. At least one a month (127(a)).",
    quorum: 'Quorum: simple majority of the committee (bye-laws 114/126).' },
  { key: 'AGM', label: 'Annual general meeting', noticeDays: 14,
    rule: "Sec. 75 — 14 clear days' notice to all members; held by 30 September.",
    quorum: 'Quorum: two-thirds of members or 20, whichever is less (bye-law 99).' },
  { key: 'SGM', label: 'Special general meeting', noticeDays: 14,
    rule: "Bye-laws 96–97 — 14 clear days' notice; within 1 month of a requisition by one-fifth of members.",
    quorum: 'Quorum: two-thirds of members or 20, whichever is less (bye-law 99).' },
];

type AgendaItem = { text: string; recusal: boolean };

export default function NewMeetingScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [kind, setKind] = React.useState<'MC' | 'AGM' | 'SGM'>('MC');
  const [title, setTitle] = React.useState('');
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('18:30');
  const [venue, setVenue] = React.useState('Society office');
  const [agenda, setAgenda] = React.useState<AgendaItem[]>([{ text: '', recusal: false }]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const meta = KINDS.find((k) => k.key === kind) ?? KINDS[0];

  const submit = async () => {
    setErr(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
      setErr(t('mtn_err_date'));
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(time.trim())) {
      setErr(t('mtn_err_time'));
      return;
    }
    const items = agenda.map((a) => ({ text: a.text.trim(), recusal: a.recusal })).filter((a) => a.text);
    if (items.length === 0) {
      setErr('Add at least one agenda item — the notice must state the business.');
      return;
    }
    setBusy(true);
    try {
      await api.post<unknown>('/api/meetings', {
        kind, title: title.trim(), venue: venue.trim(),
        scheduledAt: `${date.trim()}T${time.trim()}:00`, agenda: items,
      });
      router.replace('/meetings');
    } catch (e) {
      const er = e as { error?: string; citation?: string };
      setErr(er?.error ? (er.citation ? `${er.error} — ${er.citation}` : er.error) : t('mtn_err_generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('mtn_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ flexDirection: 'row', gap: space.s2, flexWrap: 'wrap' }}>
          {KINDS.map((k) => (
            <Chip key={k.key} label={k.label} active={kind === k.key} onPress={() => setKind(k.key)} />
          ))}
        </View>

        <View style={{
          borderRadius: radius.lg, backgroundColor: colors.canvas1,
          padding: space.s3, marginTop: space.s3,
        }}>
          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'flex-start' }}>
            <Icon name="lock" size={13} color={colors.accent700} />
            <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>{meta.rule}</AppText>
          </View>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>{meta.quorum}</AppText>
        </View>

        <View style={{ gap: space.s4, marginTop: space.s4 }}>
          <Field label={t('mtn_f_title')} help={t('mtn_f_title_help', { label: meta.label })}>
            <Input value={title} onChangeText={setTitle} placeholder={meta.label} maxLength={200} />
          </Field>
          <View style={{ flexDirection: 'row', gap: space.s3 }}>
            <View style={{ flex: 1.4 }}>
              <Field label={t('mtn_f_date')} help={`≥ ${meta.noticeDays} clear days out`}>
                <Input value={date} onChangeText={setDate} placeholder="2026-07-28" />
              </Field>
            </View>
            <View style={{ flex: 1 }}>
              <Field label={t('mtn_f_time')}>
                <Input value={time} onChangeText={setTime} placeholder="18:30" />
              </Field>
            </View>
          </View>
          <Field label={t('mtn_f_venue')}>
            <Input value={venue} onChangeText={setVenue} placeholder={t('mtn_ph_venue')} maxLength={200} />
          </Field>

          <View>
            <AppText variant="label" tone="tertiary" style={{ marginBottom: space.s2 }}>{t('mtn_agenda')}</AppText>
            <View style={{ gap: space.s2 }}>
              {agenda.map((a, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                  <Input
                    value={a.text}
                    onChangeText={(t) => setAgenda((s) => s.map((x, j) => (j === i ? { ...x, text: t } : x)))}
                    placeholder={t('mtn_ph_item', { n: i + 1 })}
                    style={{ flex: 1 }}
                    maxLength={300}
                  />
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('mtn_a11y_recusal')}
                    onPress={() => setAgenda((s) => s.map((x, j) => (j === i ? { ...x, recusal: !x.recusal } : x)))}
                    style={{
                      minHeight: 44, paddingHorizontal: space.s2, borderRadius: radius.md, borderWidth: 1,
                      alignItems: 'center', justifyContent: 'center',
                      borderColor: a.recusal ? colors.accent500 : colors.border,
                      backgroundColor: a.recusal ? colors.accent100 : 'transparent',
                    }}
                  >
                    <AppText variant="bodySm" weight="600"
                      style={{ color: a.recusal ? colors.accent700 : colors.textTertiary }}>
                      {t('mtn_recusal')}
                    </AppText>
                  </Pressable>
                  {agenda.length > 1 ? (
                    <IconButton label={t('mtn_remove_item')} onPress={() => setAgenda((s) => s.filter((_, j) => j !== i))}>
                      <Icon name="x" size={16} color={colors.textTertiary} />
                    </IconButton>
                  ) : null}
                </View>
              ))}
            </View>
            <Button
              title={t('mtn_add_item')}
              kind="secondary"
              style={{ marginTop: space.s3 }}
              onPress={() => setAgenda((s) => [...s, { text: '', recusal: false }])}
            />
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              Mark an item Recusal when a member has a personal interest — they must not be
              present for it (bye-law 120).
            </AppText>
          </View>

          {err ? (
            <AppText variant="bodySm" weight="500" style={{ color: colors.danger500 }}>{err}</AppText>
          ) : null}
          <Button title={busy ? t('mtn_scheduling') : t('mtn_title')} disabled={busy}
            style={{ marginBottom: space.s6 }} onPress={() => { void submit(); }} />
        </View>
      </Screen>
    </Screen>
  );
}
